import { useNavigate } from '@tanstack/react-router';
import { CheckCircle2 } from 'lucide-react';
import { Price } from '../../../../components/ui/price';
import type { PermitDetail } from './types';

interface Props {
  permit: PermitDetail;
  showPay: boolean;
}

interface FeeBreakdownItem {
  feeName: string;
  payee: string;
  amount: string;
  currency?: string;
}

interface FeeBreakdownData {
  breakdown?: FeeBreakdownItem[];
  totalFee?: string;
}

export function FeesTab({ permit, showPay }: Props) {
  const navigate = useNavigate();
  const feeBreakdown = permit.feeBreakdown as FeeBreakdownData | null;
  const items = feeBreakdown?.breakdown ?? [];

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-theme-sm overflow-hidden">
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Fee Breakdown</h2>
          <span className="text-sm text-gray-400">{items.length} line items</span>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-200">
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">
                  Qty
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">
                  Rate
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {items.map((fee) => {
                const amountVal = Number.parseFloat(fee.amount) || 0;
                return (
                  <tr key={fee.feeName} className="hover:bg-gray-50/30 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">
                      <div>{fee.feeName}</div>
                      <div className="text-xs text-gray-400 font-normal mt-0.5">
                        Payee: {fee.payee}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-gray-400">1</td>
                    <td className="px-6 py-4 text-right text-gray-400">
                      <Price value={amountVal} />
                    </td>
                    <td className="px-6 py-4 text-right font-semibold text-gray-900">
                      <Price value={amountVal} />
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-gray-400">
                    No fee breakdown items found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {permit.totalFee && (
          <div className="mt-6 bg-gray-50/50 rounded-xl p-6 border border-gray-200 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-500">Total Permit Fees</p>
              <p className="text-xs text-gray-400 mt-0.5">All fees are subject to review</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-900">
                <Price value={permit.totalFee} iconClassName="w-[0.7em] h-[0.7em] -mt-1" />
              </div>
            </div>
          </div>
        )}

        {showPay && (
          <div className="mt-6 flex flex-col sm:flex-row items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                Payment Ready
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                All fees calculated and ready for processing
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                navigate({ to: '/permits/$permitId/payment', params: { permitId: permit.id } })
              }
              className="w-full sm:w-auto px-5 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-xl font-medium text-sm transition-all shadow-lg shadow-brand-500/20"
            >
              Pay Now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
